import { Controller } from "@hotwired/stimulus"

/**
 * Assistant Sound Toggle Controller
 *
 * Backs the "play a sound when the assistant finishes" switch in the Assistant
 * nav dropdown. On change it persists the preference to UserConfig (PATCH) and
 * broadcasts `assistant-notifier:sound-changed` so the assistant-notifier
 * controller on <body> picks up the new value immediately, without a reload.
 *
 * The switch is a native checkbox styled via the `.checkbox-switch` CSS, so the
 * on/off appearance is handled by `peer-checked` — this controller only deals
 * with persistence and the live broadcast.
 */
export default class extends Controller {
  static values = { url: String }
  static targets = ["checkbox"]

  async toggle() {
    const enabled = this.checkboxTarget.checked

    // Optimistic: reflect the change immediately, then roll back if the save
    // fails so the UI never claims a preference that wasn't persisted.
    this._broadcast(enabled)

    const persisted = await this._persist(enabled)
    if (!persisted) this._revert(enabled)
  }

  _broadcast(enabled) {
    window.dispatchEvent(
      new CustomEvent("assistant-notifier:sound-changed", { detail: { enabled } })
    )
  }

  _revert(attempted) {
    const reverted = !attempted
    this.checkboxTarget.checked = reverted
    this._broadcast(reverted)
  }

  async _persist(enabled) {
    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this._csrfToken(),
          Accept: "application/json"
        },
        body: JSON.stringify({ user_config: { assistant_completion_sound: enabled } })
      })
      return response.ok
    } catch {
      // Network blip: treat as a failed save so the caller rolls the toggle back.
      return false
    }
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
